import { useFormikContext } from 'formik';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import CountryFlag from '../CountryFlag';
import Typography from '../Typography';
import SelectField from './SelectField';
import TextField from './TextField';
import PhoneNumberField from './PhoneNumberField';
import { useTranslation } from '../../hooks/i18n';
import { OTHER_PHONE_COUNTRY, phoneCountryCodes } from '../../utils/helpers';
import { CountryKeys } from '../../types';
import { COLORS } from '../../themev2';

interface PhoneNumberWithCountryFieldProps {
  name: string;
  countryName: string;
  label: string;
}

/**
 * Phone input with its country code selector, laid out the same way the signup does it:
 * both halves carry the label so they line up, and they share the seam in the middle.
 */
const PhoneNumberWithCountryField = ({ name, countryName, label }: PhoneNumberWithCountryFieldProps) => {
  const { t } = useTranslation(['common']);
  const { values, setFieldValue } = useFormikContext<Record<string, any>>();
  const country = values[countryName];

  return (
    <Grid container spacing={0}>
      <Grid item xs={4} md={3}>
        <SelectField
          label={label}
          name={countryName}
          options={[
            ...Object.keys(phoneCountryCodes).map((key) => ({ value: key, label: key })),
            { value: OTHER_PHONE_COUNTRY, label: t('common:signup.form.phoneNumber.otherOption') },
          ]}
          renderOption={({ value, label: optionLabel }) => (
            <MenuItem key={value} value={value}>
              {value !== OTHER_PHONE_COUNTRY && (
                <CountryFlag countryCode={value} size={24} style={{ marginRight: '8px' }} />
              )}
              {value === OTHER_PHONE_COUNTRY
                ? optionLabel
                : `(+${phoneCountryCodes[value as CountryKeys].code})`}
            </MenuItem>
          )}
          renderValue={(value: string) => (
            <Typography textAlign="center">
              {value === OTHER_PHONE_COUNTRY
                ? t('common:signup.form.phoneNumber.otherOption')
                : `(+${phoneCountryCodes[value as CountryKeys].code})`}
            </Typography>
          )}
          sx={{
            '& fieldset': {
              borderRadius: '8px 0 0 8px',
              borderColor: COLORS.gray[900],
            },
          }}
        />
      </Grid>
      <Grid item xs={8} md={9}>
        {country === OTHER_PHONE_COUNTRY ? (
          <TextField
            name={name}
            fullWidth
            label={<span style={{ visibility: 'hidden' }}>{label}</span>}
            placeholder={t('common:signup.form.phoneNumber.otherPlaceholder')}
            onChange={(value: string) => setFieldValue(name, value.replace(/\D/g, ''))}
            sx={{
              '& fieldset': {
                borderRadius: '0 8px 8px 0',
                borderColor: COLORS.gray[900],
              },
            }}
          />
        ) : (
          <PhoneNumberField
            name={name}
            label={label}
            hideLabel
            fullWidth
            value={values[name]}
            format={phoneCountryCodes[country as CountryKeys].placeholder.replace(/[0-9]/g, '#')}
            placeholder={phoneCountryCodes[country as CountryKeys].placeholder}
            onChange={(value) => setFieldValue(name, value.floatValue)}
            sx={{
              borderRadius: '0 8px 8px 0',
              borderColor: COLORS.gray[900],
            }}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default PhoneNumberWithCountryField;
